import React, { useEffect, useState } from "react";
import Modal from "@components/common/Modal/Modal";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import P from "@components/common/P/P";
import Input from "@components/common/Input/Input";
import Button from "@components/common/Button/Button";
import Spinner from "@components/Spinner";
import { TrashIcon, PhotoPlusIcon } from "@assets/icons";
import { useTranslation } from "react-i18next";

interface CompanyModalProps {
	isOpen: boolean;
	onClose: () => void;
	companyName: string;
	setCompanyName: (value: string) => void;
	companyAddress: string;
	setCompanyAddress: (value: string) => void;
	phone: string;
	setPhone: (value: string) => void;
	email: string;
	setEmail: (value: string) => void;
	loginType: "phone" | "email";
	image: string | Blob | null;
	handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleEditImage: () => void;
	handleDeleteImage: () => void;
	handleSave: () => void | Promise<void>;
	fileInputRef: React.RefObject<HTMLInputElement>;
}

const CompanyModal: React.FC<CompanyModalProps> = ({
	isOpen,
	onClose,
	companyName,
	setCompanyName,
	companyAddress,
	setCompanyAddress,
	phone,
	setPhone,
	email,
	setEmail,
	loginType,
	image,
	handleFileChange,
	handleEditImage,
	handleDeleteImage,
	handleSave,
	fileInputRef,
}) => {
	const { t } = useTranslation();

	const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (image instanceof File) {
			const objectUrl = URL.createObjectURL(image);
			setPreviewUrl(objectUrl);

			return () => {
				URL.revokeObjectURL(objectUrl);
			};
		}

		setPreviewUrl(typeof image === "string" ? image : undefined);

		return undefined;
	}, [image]);

	useEffect(() => {
		if (!isOpen) {
			setIsSaving(false);
		}
	}, [isOpen]);

	const isPhoneLogin = loginType === "phone";
	const isEmailLogin = loginType === "email";

	const onSave = async () => {
		if (isSaving) return;

		try {
			setIsSaving(true);
			await Promise.resolve(handleSave());
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			header={t("platform.companyModal.header")}
		>
			<div className="grid grid-flow-row grid-cols-1 gap-4 auto-rows-max">
				{image ? (
					<div className="grid grid-cols-1 sm:grid-cols-maxFr items-start sm:items-center gap-4">
						<div className="grid grid-cols-1 place-items-center place-content-center p-2 border w-20 h-20 cursor-pointer rounded-xl border-light-stroke dark:border-dark-stroke mx-auto sm:mx-0">
							<img
								src={previewUrl}
								alt={t("platform.companyModal.logoAlt")}
								className="object-contain w-20 max-h-16"
							/>
						</div>

						<div className="grid grid-cols-1 gap-4 grid-rows-maxTwo place-content-between w-full text-center sm:text-left">
							<P
								size="p3"
								type="semibold"
								className="text-light-textColor dark:text-dark-textColor break-words"
							>
								{t("platform.companyModal.uploadLogo")}
							</P>

							<div className="grid grid-cols-1 minPhone:grid-cols-frMax gap-2 sm:gap-1.5 w-full">
								<Button
									appearance="primary"
									rounded="rounded-xl"
									width="full"
									onClick={handleEditImage}
									disabled={isSaving}
								>
									{t("platform.companyModal.actions.change")}
								</Button>

								<div
									className={`grid place-items-center p-3 transition-all duration-300 rounded-xl w-full minPhone:w-max justify-self-stretch minPhone:justify-self-start ${
										isSaving
											? "opacity-50 cursor-not-allowed"
											: "cursor-pointer bg-light-grayOne text-light-textColor dark:text-dark-textColor dark:bg-dark-grayOne hover:bg-light-grayTwo"
									}`}
									onClick={isSaving ? undefined : handleDeleteImage}
								>
									<SvgIcon Icon={TrashIcon} width={22} height={22} />
								</div>
							</div>
						</div>
					</div>
				) : (
					<div className="grid grid-cols-1 sm:grid-cols-maxFr items-center gap-4">
						<div
							className={`grid place-items-center p-5 rounded-xl border-light-grayOne dark:border-dark-grayOne bg-light-secondaryColor dark:bg-dark-secondaryColor mx-auto sm:mx-0 ${
								isSaving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
							}`}
							onClick={
								isSaving ? undefined : () => fileInputRef.current?.click()
							}
						>
							<SvgIcon Icon={PhotoPlusIcon} width={32} height={32} />
						</div>

						<P
							size="p3"
							type="semibold"
							className="text-light-textColor dark:text-dark-textColor text-center sm:text-left break-words"
						>
							{t("platform.companyModal.uploadLogo")}
						</P>
					</div>
				)}

				<input
					ref={fileInputRef}
					id="image-upload"
					type="file"
					accept="image/*"
					className="hidden"
					onChange={handleFileChange}
					disabled={isSaving}
				/>

				<Input
					type="text"
					label={t("platform.companyModal.fields.companyName")}
					value={companyName}
					onChange={(e) => setCompanyName(e.target.value)}
					disabled={isSaving}
				/>

				<Input
					type="text"
					label={t("platform.companyModal.fields.companyAddress")}
					value={companyAddress}
					onChange={(e) => setCompanyAddress(e.target.value)}
					disabled={isSaving}
				/>

				<Input
					type="text"
					label={t("platform.companyModal.fields.phone")}
					value={phone}
					onChange={(e) => setPhone(e.target.value)}
					disabled={isSaving || isPhoneLogin}
				/>

				<Input
					type="text"
					label={t("platform.companyModal.fields.email")}
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					disabled={isSaving || isEmailLogin}
				/>

				<div className="grid grid-cols-1 minPhone:grid-cols-2 gap-3">
					<Button
						appearance="secondary"
						fontSize="t1"
						textType="semibold"
						rounded="rounded-xl"
						width="full"
						onClick={onClose}
						disabled={isSaving}
					>
						{t("common.cancel")}
					</Button>

					<Button
						appearance="primary"
						fontSize="t1"
						textType="semibold"
						rounded="rounded-xl"
						width="full"
						onClick={onSave}
						disabled={isSaving}
						icon={
							isSaving ? (
								<Spinner
									size={18}
									color="text-light-grayOne dark:text-dark-grayOne"
								/>
							) : undefined
						}
					>
						{t("platform.companyModal.actions.create")}
					</Button>
				</div>
			</div>
		</Modal>
	);
};

export default React.memo(CompanyModal);
